// @ts-nocheck
import React, { useEffect, useState } from 'react';
import api from '../services/api';
import notify from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import ActionButtons from '../components/ActionButtons';

const STATUS_BADGE = {
    new: 'secondary',
    contacted: 'info',
    interested: 'primary',
    site_visit: 'warning',
    negotiation: 'dark',
    converted: 'success',
    lost: 'danger',
};

const emptyForm = () => ({
    name: '',
    email: '',
    phone: '',
    source: 'website',
    status: 'new',
    interested_project_id: '',
    notes: '',
});

export default function Leads() {
    const { can } = useAuth();
    const { t, td } = useI18n();
    const [leads, setLeads] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(emptyForm());

    const closeLeadModal = () => {
        setShowModal(false);
        setForm(emptyForm());
    };

    const load = () => {
        setLoading(true);
        Promise.all([api.get('/leads'), api.get('/projects')])
            .then(([l, p]) => {
                setLeads(l.data.data);
                setProjects(p.data.data);
            })
            .catch((err) => notify.apiError(err, t('failed')))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
    }, []);

    const handleCreate = async (e) => {
        e.preventDefault();
        try {
            await api.post('/leads', {
                ...form,
                interested_project_id: form.interested_project_id || null,
            });
            notify.success(t('successCreate'));
            closeLeadModal();
            load();
        } catch (err) {
            notify.apiError(err, t('failed'));
        }
    };

    const updateStatus = async (id, status) => {
        try {
            await api.put(`/leads/${id}`, { status });
            notify.success(t('successUpdate'));
            load();
        } catch (err) {
            notify.apiError(err, t('failed'));
        }
    };

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                    <h4 className="mb-1">{t('crm')}</h4>
                    <p className="text-muted mb-0">{t('leads')}</p>
                </div>
                {can('leads.create') && (
                    <button className="btn btn-primary" onClick={() => setShowModal(true)}>
                        <i className="bi bi-person-plus me-1" />
                        {t('newLead')}
                    </button>
                )}
            </div>

            <div className="card table-card">
                <div className="table-responsive">
                    <table className="table table-hover mb-0">
                        <thead className="table-light">
                            <tr>
                                <th>{t('name')}</th>
                                <th>{t('phone')}</th>
                                <th>{t('source')}</th>
                                <th>{t('interestProject')}</th>
                                <th>{t('assigned')}</th>
                                <th>{t('status')}</th>
                                <th>{t('actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">
                                        <div className="spinner-border spinner-border-sm text-success" />
                                    </td>
                                </tr>
                            ) : leads.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center text-muted py-4">
                                        {t('noData')}
                                    </td>
                                </tr>
                            ) : (
                                leads.map((l) => (
                                    <tr key={l.id}>
                                        <td>
                                            <div className="fw-semibold">{l.name}</div>
                                            <small className="text-muted">{l.email}</small>
                                        </td>
                                        <td>{l.phone}</td>
                                        <td>{td('lead_source', (l.source || '').toLowerCase().replace(/\s+/g, '_')) || l.source || '—'}</td>
                                        <td>{l.interestedProject?.name || '—'}</td>
                                        <td>{l.assignedExecutive?.name || '—'}</td>
                                        <td>
                                            <span className={`badge bg-${STATUS_BADGE[l.status] || 'secondary'} badge-status`}>
                                                {td('status', l.status) || l.status?.replace(/_/g, ' ')}
                                            </span>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center gap-1">
                                                {can('leads.edit') ? (
                                                    <select
                                                        className="form-select form-select-sm"
                                                        style={{ width: 140 }}
                                                        value={l.status}
                                                        onChange={(e) => updateStatus(l.id, e.target.value)}
                                                    >
                                                        {Object.keys(STATUS_BADGE).map((s) => (
                                                            <option key={s} value={s}>
                                                                {td('status', s) || s.replace(/_/g, ' ')}
                                                            </option>
                                                        ))}
                                                    </select>
                                                ) : (
                                                    <span className={`badge bg-${STATUS_BADGE[l.status] || 'secondary'}`}>
                                                        {td('status', l.status) || l.status}
                                                    </span>
                                                )}
                                                <ActionButtons
                                                    canDelete={can('leads.delete')}
                                                    onDelete={async () => {
                                                        if (!window.confirm(t('confirmDelete'))) return;
                                                        try {
                                                            await api.delete(`/leads/${l.id}`);
                                                            notify.success(t('successDelete'));
                                                            load();
                                                        } catch (err) {
                                                            notify.apiError(err, t('failed'));
                                                        }
                                                    }}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {showModal && (
                <div
                    className="modal show d-block app-modal"
                    onClick={closeLeadModal}
                    style={{ background: 'rgba(0,0,0,0.5)' }}
                >
                    <div className="modal-dialog modal-dialog-centered" onClick={(e) => e.stopPropagation()}>
                        <form className="modal-content" onSubmit={handleCreate}>
                            <div className="modal-header">
                                <h5 className="modal-title">{t('newLead')}</h5>
                                <button type="button" className="btn-close" onClick={closeLeadModal} />
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">{t('name')} *</label>
                                    <input
                                        className="form-control"
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('phone')} *</label>
                                    <input
                                        className="form-control"
                                        required
                                        value={form.phone}
                                        onChange={(e) => setForm({ ...form, phone: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('email')}</label>
                                    <input
                                        type="email"
                                        className="form-control"
                                        value={form.email}
                                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('source')}</label>
                                    <select
                                        className="form-select"
                                        value={form.source}
                                        onChange={(e) => setForm({ ...form, source: e.target.value })}
                                    >
                                        <option value="website">{t('website')}</option>
                                        <option value="walk_in">{t('walkIn')}</option>
                                        <option value="referral">{t('referral')}</option>
                                        <option value="social_media">{t('socialMedia')}</option>
                                        <option value="other">{t('other')}</option>
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('interestProject')}</label>
                                    <select
                                        className="form-select"
                                        value={form.interested_project_id}
                                        onChange={(e) => setForm({ ...form, interested_project_id: e.target.value })}
                                    >
                                        <option value="">—</option>
                                        {projects.map((p) => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="mb-3">
                                    <label className="form-label">{t('notes')}</label>
                                    <textarea
                                        className="form-control"
                                        rows={2}
                                        value={form.notes}
                                        onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={closeLeadModal}>
                                    {t('cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary">
                                    {t('save')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}